import React, { FC } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { keyframes } from '@mui/system';

/** Gentle idle float with a small head tilt — empty states. */
const bob = keyframes`
  0%, 100% { transform: translateY(0) rotate(-3deg); }
  50% { transform: translateY(-7px) rotate(3deg); }
`;

/** Continuous spin — the horn spiral doubles as a loading spinner. */
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

/** Slow breathing while dozing — end of feed. */
const breathe = keyframes`
  0%, 100% { transform: rotate(8deg) scale(1); }
  50% { transform: rotate(8deg) scale(1.06); }
`;

/** Puzzled head shake — 404 / errors. */
const wobble = keyframes`
  0%, 100% { transform: rotate(-7deg); }
  50% { transform: rotate(7deg); }
`;

/** Floating marks (z's while sleeping, ? when confused). */
const floatAway = keyframes`
  0% { opacity: 0; transform: translate(0, 0) scale(0.7); }
  30% { opacity: 1; }
  100% { opacity: 0; transform: translate(10px, -24px) scale(1.2); }
`;

export type GoatMascotVariant = 'bob' | 'spin' | 'sleep' | 'confused' | 'static';

interface GoatMascotProps {
  /** Animation personality. `static` renders without motion. */
  variant?: GoatMascotVariant;
  /** Rendered square size in px. */
  size?: number;
  /**
   * Extra static rotation in degrees — used by pull-to-refresh to wind the
   * horn spiral along with the pull distance.
   */
  rotate?: number;
}

const ANIMATION: Record<GoatMascotVariant, string | undefined> = {
  bob: `${bob} 2.6s ease-in-out infinite`,
  spin: `${spin} 0.9s linear infinite`,
  sleep: `${breathe} 3.2s ease-in-out infinite`,
  confused: `${wobble} 1.6s ease-in-out infinite`,
  static: undefined,
};

/**
 * The GOAT mascot — the goat-head/horn mark lifted from the brand logo, with
 * small CSS-only personalities: bobbing (empty screens), spinning (refresh),
 * dozing with z's (end of feed) and a puzzled wobble with a floating "?"
 * (404/errors). Honors prefers-reduced-motion by dropping the animation.
 */
const GoatMascot: FC<GoatMascotProps> = ({ variant = 'bob', size = 72, rotate = 0 }) => {
  const theme = useTheme() as any;
  const primary = theme.palette.logo?.primary ?? theme.palette.primary.main;
  const secondary = theme.palette.logo?.secondary ?? theme.palette.text.primary;

  const marks =
    variant === 'sleep' ? ['z', 'z', 'z'] : variant === 'confused' ? ['?'] : [];

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          lineHeight: 0,
          transform: rotate ? `rotate(${rotate}deg)` : undefined,
          animation: ANIMATION[variant],
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        <svg viewBox="0 0 155 180" width="100%" height="100%" aria-hidden focusable="false">
          <g>
            <path fill={secondary} d="M123.83,138c1.95-8.6,2.84-19.07.84-31.11-4.38-26.28-23-47.97-23-47.97-12.92-18.18-47.92-28.28-68.34-8.74-13.09,12.53-11.95,34.95,4.97,43.66,13.92,7.17,32.26-3.53,34.77-18.4-.69,9-4.07,25.26-19.23,28.48-11.36,2.42-23.92-.04-32.23-8.52-8.89-9.07-11.29-22.95-9.27-35.12,1.99-11.97,10.84-21.14,20.72-27.56,20.35-13.2,53.88-8.5,71.28,7.93,14.57,13.76,37.71,51.21,19.49,97.35Z" />
            <path fill={primary} d="M144.61,70.33C147.13,13.69,96.63,0,96.63,0c28.91,2.63,42.49,17.74,53.44,33.29,10.95,15.55,13.36,44.9,2.63,80.17-10.73,35.26-33.07,65.93-33.07,65.93l-31.54-20.81c2.61-24.14-12.77-44.82-12.99-45.12,23.14,10.07,23.95,35.48,23.95,35.48l16.43,11.17c2.25-4.39,6.05-11.94,8.36-22.11,18.22-46.14-4.92-83.58-19.49-97.35-17.4-16.43-50.93-21.14-71.28-7.93-9.88,6.42-18.73,15.58-20.72,27.56-2.02,12.17.38,26.05,9.27,35.12,8.32,8.49,20.87,10.94,32.23,8.52,15.17-3.22,18.54-19.48,19.23-28.48.33-1.94.38-3.96.13-6.01,6.91,54.84-35.92,51.79-56.21,35.63C-3.75,88.55-8.85,50.69,20.85,23.03,42.1,3.24,80.04,2.9,103.72,18.65c12.75,8.48,23.9,19.81,31.95,32.84,2.71,4.39,9.07,13.19,8.94,18.84Z" />
            <path fill={primary} d="M112.08,135.84l-.16-.55c.06.19.12.37.16.55Z" />
            <path fill={primary} d="M99.66,92.79c.38,1.03,12.37,34.05,12.42,43.05-.04-.18-.1-.36-.16-.55h0s-12.26-42.5-12.26-42.5Z" />
            <path fill={primary} d="M99.65,92.77c14.6,13.59,12.43,43.08,12.43,43.08-.05-9.01-12.05-42.02-12.42-43.05v-.02Z" />
            <path fill={secondary} d="M99.65,92.77v.02s12.27,42.49,12.27,42.49c-2.03-5.93-15.45-18.67-15.45-18.67l3.18-23.85Z" />
          </g>
        </svg>
      </Box>

      {marks.map((mark, i) => (
        <Box
          key={i}
          component="span"
          sx={{
            position: 'absolute',
            top: variant === 'sleep' ? '4%' : '-12%',
            right: variant === 'sleep' ? `${-4 + i * 12}%` : '-8%',
            fontFamily: theme.tokens.typography.displayFontFamily,
            fontSize: size * (variant === 'sleep' ? 0.2 : 0.34),
            color: primary,
            opacity: 0,
            animation: `${floatAway} 2.4s ease-out ${i * 0.7}s infinite`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0.7 },
            pointerEvents: 'none',
          }}
        >
          {mark}
        </Box>
      ))}
    </Box>
  );
};

export default GoatMascot;
